import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useScroll, useSpring } from 'framer-motion';
import { ArrowUpRight, Calendar, MapPin, Building2, Target, CheckCircle, ChevronDown, ChevronUp, Download, FileText } from 'lucide-react';
import BackToTopButton from '../components/BackToTopButton';
import InternshipTechStack from '../components/ui/internship-tech-stack';



const STAGE_DATA = {
  hero: {
    label: "internship.26",
    title: "INTERNSHIP",
    location: "Best, Netherlands",
    period: "February - May 2026",
    description: "Software developer internship at Matas Electronics BV, a PCB assembly specialist founded in 1984. Building an automated backup monitoring and reporting system to replace manual daily IT checks.",
  },

  company: {
    name: "Matas Electronics BV",
    founded: "1984",
    location: "Best, Netherlands",
    sector: "Electronic Manufacturing Services (EMS)",
    description: "Matas Electronics BV specialises in the assembly and manufacturing of high-quality printed circuit boards (PCBs). As an EMS partner, Matas works closely with clients to assemble PCBs to specification, with a strong emphasis on quality, flexibility and direct communication.",
    website: "https://www.matas.nl",
  },

  project: {
    title: "Automated Backup Monitoring System",
    subtitle: "Replacing manual daily IT verification with a smart dashboard",
    description: "The IT department at Matas receives a daily email from their Veeam backup system. A staff member manually checks the email, analyses the data and reports the findings to the internal intranet. This process is labour-intensive, error-prone and slow. My task was to automate it entirely.",
    goal: "Build a system that automatically fetches backup data via the Veeam REST API, processes it, stores it in a database and presents it through a clear dashboard, with automatic action items on failure and a formal approval workflow.",
  },

  sprints: [
    {
      number: "SPRINT 0",
      title: "Preparation & Practice Assignment",
      period: "Week 1",
      status: "completed",
      description: "Onboarding at Matas, setting up the development environment and completing a practice assignment. This gave my mentor insight into my way of working and allowed me to receive early feedback.",
      tasks: [
        "Development environment setup",
        "Practice assignment completed",
        "First feedback from internship mentor",
        "Familiarisation with Matas IT infrastructure"
      ]
    },
    {
      number: "SPRINT 1",
      title: "Concept & Research",
      period: "Week 2",
      status: "completed",
      description: "Researching the Veeam REST API, the existing backup workflow and the requirements from the IT department. Defining the functional and non-functional requirements with stakeholders.",
      tasks: [
        "Veeam REST API documentation study",
        "Stakeholder interviews (IT lead Rob, IT staff)",
        "Functional requirements finalised",
        "Technical architecture decided"
      ]
    },
    {
      number: "SPRINT 2",
      title: "Realisation – Core System",
      period: "Weeks 3–7",
      status: "completed",
      description: "Building the core of the system: API integration with Veeam, database schema, automatic action item creation on backup failure and the initial dashboard.",
      tasks: [
        "Veeam REST API integration (Vue.js / PHP)",
        "Database design and implementation",
        "Automatic action item generation on failure",
        "Dashboard: server overview with backup statuses",
        "Demo meeting Week 4, feedback processed"
      ]
    },
    {
      number: "SPRINT 3",
      title: "Realisation – Approval & Finalisation",
      period: "Weeks 8–12",
      status: "completed",
      description: "Adding the approval workflow for the IT lead, resolution tracking, audit trail and final polishing of the dashboard UI. Second demo meeting in week 8.",
      tasks: [
        "Approval workflow (IT lead Rob)",
        "Resolution step tracking per action item",
        "Audit trail for ISO compliance",
        "Demo meeting Week 8, final feedback",
        "Dashboard filtering and UX improvements"
      ]
    },
    {
      number: "SPRINT 4",
      title: "Final Delivery",
      period: "Week 13",
      status: "completed",
      description: "Final handover of the completed system, documentation, deployment guide and a presentation to the Matas IT team.",
      tasks: [
        "Final system handover",
        "Technical documentation",
        "Deployment and usage guide",
        "Presentation to IT team"
      ]
    },
  ],

  // Note: This techStack data is no longer used in rendering
  // The InternshipTechStack component has its own icon definitions
  techStack: [
    { name: "VUE.JS / PHP", category: "Backend" },
    { name: "Veeam REST API", category: "Integration" },
    { name: "SQL Server", category: "Database" },
    { name: "Dashboard UI", category: "Frontend" },
    { name: "Azure", category: "Cloud" },
    { name: "Git / Gitlab", category: "Version Control" },
  ],

  requirements: {
    functional: [
      "Automatic daily backup status report via Veeam API",
      "Dashboard with filters for servers, jobs and errors",
      "Auto-generated action items when a backup fails",
      "Resolution tracking with timestamps and responsible staff",
      "Formal approval workflow for the IT lead (Rob)",
      "Intranet integration for daily reporting",
    ],
    nonFunctional: [
      "Access restricted to IT department only",
      "Only the IT lead can formally approve resolutions",
      "All actions logged for ISO compliance and audits",
      "Intuitive dashboard with clear open/closed action items",
      "Scalable: supports additional servers and backup jobs",
      "Seamless integration with existing Veeam and intranet systems",
    ],
  },

  stakeholders: [
    { role: "IT Department Lead", name: "Rob", responsibility: "Final approval of resolved backup failures" },
    { role: "IT Senior Staff", name: "Rik", responsibility: "Internship supervisor" },
  ],

  ticker: ["MATAS ELECTRONICS", "INTERNSHIP 2026", "BEST, NETHERLANDS", "VUE.JS", "INERTIA.JS", "VEEAM API", "BACKUP AUTOMATION"],
};


function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 100, damping: 30 });
  return (
    <motion.div
      style={{ scaleX }}
      className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 via-amber-500 to-orange-600 origin-left z-[100]"
    />
  );
}

function StatusBadge({ status }) {
  const config = {
    completed:    { label: "COMPLETED",   bg: "bg-green-500/20",  border: "border-green-500/40",  text: "text-green-400"  },
    "in-progress":{ label: "IN PROGRESS", bg: "bg-orange-500/20", border: "border-orange-500/40", text: "text-orange-400" },
    upcoming:     { label: "UPCOMING",    bg: "bg-white/10",      border: "border-white/20",      text: "text-white/50"   },
  };

  // Normalize status to lowercase and fallback to 'upcoming' if not found
  const normalizedStatus = status?.toLowerCase() || 'upcoming';
  const c = config[normalizedStatus] || config.upcoming;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-silkscreen border ${c.bg} ${c.border} ${c.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${normalizedStatus === 'completed' ? 'bg-green-400' : normalizedStatus === 'in-progress' ? 'bg-orange-400 animate-pulse' : 'bg-white/30'}`} />
      {c.label}
    </span>
  );
}

function SprintCard({ sprint, index }) {
  const [open, setOpen] = useState(index < 3);
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.08 }}
      className="group"
    >
      <div className={`relative backdrop-blur-xl bg-white/[0.03] border rounded-[24px] sm:rounded-[28px] overflow-hidden transition-all duration-300 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] ${
        sprint.status === 'in-progress' ? 'border-orange-500/30' : 'border-white/10 hover:border-white/20'
      }`}>
        {/* Left accent bar */}
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${
          sprint.status === 'completed' ? 'bg-green-500' :
          sprint.status === 'in-progress' ? 'bg-orange-500' : 'bg-white/20'
        }`} />

        <button
          onClick={() => setOpen(!open)}
          className="w-full text-left p-6 sm:p-8 flex items-start justify-between gap-4"
        >
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 flex-1 min-w-0">
            <span className="text-orange-500 text-xs font-silkscreen flex-shrink-0">{sprint.number}</span>
            <div className="min-w-0">
              <h3 className="text-white font-silkscreen text-base sm:text-lg break-words">{sprint.title}</h3>
              <span className="text-white/40 text-xs font-silkscreen mt-1 block">{sprint.period}</span>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <StatusBadge status={sprint.status} />
            <div className="text-white/40 group-hover:text-white/70 transition-colors">
              {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </div>
        </button>

        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="px-6 sm:px-8 pb-6 sm:pb-8 border-t border-white/5"
          >
            <p className="text-white/60 text-sm leading-relaxed mt-5 mb-5">{sprint.description}</p>
            <ul className="space-y-2">
              {sprint.tasks.map((task, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-white/70">
                  <CheckCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${
                    sprint.status === 'completed' ? 'text-green-400' :
                    sprint.status === 'in-progress' ? 'text-orange-400' : 'text-white/20'
                  }`} />
                  {task}
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}


function InternshipPage() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-[#0a0a0a] min-h-screen"
    >
      <ProgressBar />

      <section className="relative pt-24 sm:pt-32 pb-16 sm:pb-24 px-4 sm:px-6 lg:px-12 overflow-hidden">
        {/* Background glow */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-orange-500 text-xs sm:text-sm tracking-widest mb-4 sm:mb-6 block font-silkscreen"
            >
              {STAGE_DATA.hero.label}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-5xl sm:text-6xl lg:text-7xl font-silkscreen text-white leading-none mb-4"
            >
              {STAGE_DATA.hero.title}
  
            </motion.h1>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap items-center gap-4 sm:gap-8 mt-8 mb-8"
            >
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <MapPin className="w-4 h-4 text-orange-500" />
                <span className="font-silkscreen text-xs">{STAGE_DATA.hero.location}</span>
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Calendar className="w-4 h-4 text-orange-500" />
                <span className="font-silkscreen text-xs">{STAGE_DATA.hero.period}</span>
              </div>
              <div className="flex items-center gap-2 text-white/50 text-sm">
                <Building2 className="w-4 h-4 text-orange-500" />
                <span className="font-silkscreen text-xs">EMS / PCB Manufacturing</span>
              </div>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-white/60 text-base sm:text-lg max-w-2xl leading-relaxed"
            >
              {STAGE_DATA.hero.description}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <div className="overflow-hidden border-y border-white/5 py-3 bg-[#111]">
        <motion.div
          animate={{ x: [0, -2000] }}
          transition={{ duration: 25, repeat: Infinity, ease: "linear" }}
          className="flex gap-8 whitespace-nowrap"
        >
          {[...STAGE_DATA.ticker, ...STAGE_DATA.ticker, ...STAGE_DATA.ticker].map((item, i) => (
            <span key={i} className="text-white/20 text-xs font-silkscreen tracking-widest">{item}</span>
          ))}
        </motion.div>
      </div>

      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-start">
            {/* Left */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">THE COMPANY</span>
              <h2 className="text-3xl sm:text-5xl font-silkscreen text-white mb-6">
                MATAS<br /><span className="text-white/30">ELECTRONICS BV</span>
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">{STAGE_DATA.company.description}</p>
              <a
                href={STAGE_DATA.company.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-orange-500 font-silkscreen text-sm hover:text-orange-400 transition-colors"
              >
                VISIT WEBSITE
                <ArrowUpRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Right - Info cards */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              {[
                { label: "Founded", value: STAGE_DATA.company.founded, icon: Calendar },
                { label: "Location", value: "Best, NL", icon: MapPin },
                { label: "Sector", value: "EMS / PCB", icon: Building2 },
                { label: "Role", value: "Software Developer Intern", icon: Target },
              ].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-[20px] p-5 hover:border-orange-500/30 transition-colors shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]"
                >
                  <item.icon className="w-5 h-5 text-orange-500 mb-3" />
                  <p className="text-white/40 text-[10px] font-silkscreen tracking-widest mb-1">{item.label}</p>
                  <p className="text-white text-sm font-silkscreen">{item.value}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-5 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <div className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-[32px] sm:rounded-[40px] p-8 sm:p-12 lg:p-16 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-10"
            >
              <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">THE PROJECT</span>
              <h2 className="text-2xl sm:text-4xl font-silkscreen text-white mb-2">{STAGE_DATA.project.title}</h2>
              <p className="text-white/40 font-silkscreen text-sm">{STAGE_DATA.project.subtitle}</p>
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h3 className="text-white/50 text-xs font-silkscreen tracking-widest mb-3">SITUATION</h3>
                <p className="text-white/70 leading-relaxed text-sm sm:text-base">{STAGE_DATA.project.description}</p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                <h3 className="text-white/50 text-xs font-silkscreen tracking-widest mb-3">MY GOAL</h3>
                <p className="text-white/70 leading-relaxed text-sm sm:text-base">{STAGE_DATA.project.goal}</p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">INTERNSHIP DOCUMENTS</span>
            <h2 className="text-2xl sm:text-4xl font-silkscreen text-white">DOWNLOADS</h2>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-4">
            {[
              {
                title: "Project Plan",
                description: "Initial planning, scope definition and sprint structure for the internship project.",
                file: "/images/ProjectPlan.pdf",
                filename: "ProjectPlan_Bekirhan_Simsek.pdf",
                accent: "orange",
              },
              {
                title: "Realization Document",
                description: "Full thesis report, covering the entire development process.",
                file: "/images/RealisatieDocument.pdf",
                filename: "RealisatieDocument_Bekirhan_Simsek.pdf",
                accent: "blue",
              },
              {
                title: "Reflection",
                description: "Personal reflection on the internship experience, learnings and professional growth.",
                file: "/images/ReflectieDocument.pdf",
                filename: "Reflectie_Bekirhan_Simsek.pdf",
                accent: "green",
              },
            ].map((doc, i) => {
              const accentMap = {
                orange: { border: "hover:border-orange-500/50", icon: "bg-orange-500/10 border-orange-500/20", text: "text-orange-500", btn: "bg-orange-500 hover:bg-orange-600 shadow-orange-500/20" },
                blue:   { border: "hover:border-blue-500/50",   icon: "bg-blue-500/10 border-blue-500/20",   text: "text-blue-400",   btn: "bg-blue-500 hover:bg-blue-600 shadow-blue-500/20" },
                green:  { border: "hover:border-green-500/50",  icon: "bg-green-500/10 border-green-500/20", text: "text-green-400",  btn: "bg-green-600 hover:bg-green-700 shadow-green-500/20" },
              };
              const a = accentMap[doc.accent];
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className={`backdrop-blur-xl bg-white/[0.03] border border-white/10 ${a.border} rounded-[24px] p-6 sm:p-8 transition-all shadow-[0_8px_32px_0_rgba(0,0,0,0.37)] flex flex-col gap-4`}
                >
                  <div className={`w-12 h-12 rounded-xl border ${a.icon} flex items-center justify-center flex-shrink-0`}>
                    <FileText className={`w-5 h-5 ${a.text}`} />
                  </div>
                  <div className="flex-1">
                    <p className={`${a.text} text-[10px] font-silkscreen tracking-widest mb-1`}>PDF DOCUMENT</p>
                    <h3 className="text-white font-silkscreen text-sm sm:text-base mb-2">{doc.title}</h3>
                    <p className="text-white/50 text-xs leading-relaxed">{doc.description}</p>
                  </div>
                  <div className="flex gap-2">
                    <a
                      href={doc.file}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`flex-1 inline-flex items-center justify-center gap-2 ${a.btn} text-white px-4 py-2.5 rounded-xl font-silkscreen text-xs transition-all shadow-lg`}
                    >
                      <FileText className="w-3.5 h-3.5" />
                      OPEN
                    </a>
                    <a
                      href={doc.file}
                      download={doc.filename}
                      className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white/70 hover:text-white px-4 py-2.5 rounded-xl font-silkscreen text-xs transition-all"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      <InternshipTechStack />

      <section className="py-5 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">PROJECT PLANNING</span>
            <h2 className="text-2xl sm:text-4xl font-silkscreen text-white mb-2">SPRINT OVERVIEW</h2>
            <p className="text-white/40 text-sm">Feb 23, 2026 – May 22, 2026 · 13 weeks</p>
          </motion.div>

          <div className="space-y-4">
            {STAGE_DATA.sprints.map((sprint, i) => (
              <SprintCard key={i} sprint={sprint} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-20 px-4 sm:px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">SPECIFICATIONS</span>
            <h2 className="text-2xl sm:text-4xl font-silkscreen text-white">REQUIREMENTS</h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Functional */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-[28px] p-8 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]"
            >
              <h3 className="text-white font-silkscreen text-sm mb-6 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />
                FUNCTIONAL
              </h3>
              <ul className="space-y-4">
                {STAGE_DATA.requirements.functional.map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/60">
                    <CheckCircle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Non-functional */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-[28px] p-8 shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]"
            >
              <h3 className="text-white font-silkscreen text-sm mb-6 flex items-center gap-3">
                <span className="w-2 h-2 rounded-full bg-blue-400 inline-block" />
                NON-FUNCTIONAL
              </h3>
              <ul className="space-y-4">
                {STAGE_DATA.requirements.nonFunctional.map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm text-white/60">
                    <CheckCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                    {req}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-5 px-4 sm:px-6 lg:px-12 pb-20">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <span className="text-orange-500 text-xs tracking-widest font-silkscreen block mb-4">PEOPLE INVOLVED</span>
            <h2 className="text-2xl sm:text-4xl font-silkscreen text-white">STAKEHOLDERS</h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {STAGE_DATA.stakeholders.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
                className="backdrop-blur-xl bg-white/[0.03] border border-white/10 rounded-[24px] p-6 hover:border-orange-500/30 transition-colors group shadow-[0_8px_32px_0_rgba(0,0,0,0.37)]"
              >
                <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4 group-hover:bg-orange-500/20 transition-colors">
                  <span className="text-orange-500 font-silkscreen text-sm font-bold">{s.name.charAt(0)}</span>
                </div>
                <p className="text-orange-500 text-[10px] font-silkscreen tracking-widest mb-1">{s.role}</p>
                <p className="text-white font-silkscreen text-sm mb-3">{s.name}</p>
                <p className="text-white/50 text-xs leading-relaxed">{s.responsibility}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <BackToTopButton />
    </motion.div>
  );
}

export default InternshipPage;